import { Search, RefreshCw } from 'lucide-react';
import { Button } from '../ui';
import type { DashboardSection } from '../layout/Sidebar';
import type { IssueFilter } from '../../hooks/useDashboardState';

interface QueueHeaderProps {
  section: DashboardSection;
  totalCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRefresh: () => void;
  refreshCountdown?: number;
  // Issue filter props
  issueFilter?: IssueFilter;
  onIssueFilterChange?: (filter: IssueFilter) => void;
  issueCounts?: {
    lost: number;
    found: number;
  };
  // Action button props
  selectedCount?: number;
  onDownloadSelected?: () => void;
  onTranslateSelected?: () => void;
  onUploadSelected?: () => void;
  onClearSelection?: () => void;
  isDownloading?: boolean;
  isTranslating?: boolean;
  isUploading?: boolean;
}

const SECTION_TITLES: Record<DashboardSection, string> = {
  new: 'New in Salesforce',
  queue: 'Translation Queue',
  approved: 'Approved Cases',
  uploaded: 'Uploaded Cases',
  returned: 'Returned Cases',
  issues: 'Issues',
  other: 'Other Cases',
};

export function QueueHeader({
  section,
  totalCount,
  searchQuery,
  onSearchChange,
  onRefresh,
  refreshCountdown,
  issueFilter,
  onIssueFilterChange,
  issueCounts,
  selectedCount = 0,
  onDownloadSelected,
  onTranslateSelected,
  onUploadSelected,
  onClearSelection,
  isDownloading,
  isTranslating,
  isUploading,
}: QueueHeaderProps) {
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              {SECTION_TITLES[section]}
              <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">({totalCount})</span>
            </h2>
            {section === 'other' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Cases handled outside this interface (e.g., sent to external translators or completed manually)
              </p>
            )}
          </div>

          {/* Issue sub-filters */}
          {section === 'issues' && issueCounts && onIssueFilterChange && (
            <div className="flex items-center gap-1 ml-4">
              <FilterButton
                label="All"
                count={issueCounts.lost + issueCounts.found}
                isActive={issueFilter === 'all'}
                onClick={() => onIssueFilterChange('all')}
              />
              <FilterButton
                label="Lost"
                count={issueCounts.lost}
                isActive={issueFilter === 'lost'}
                onClick={() => onIssueFilterChange('lost')}
              />
              <FilterButton
                label="Found"
                count={issueCounts.found}
                isActive={issueFilter === 'found'}
                onClick={() => onIssueFilterChange('found')}
              />
            </div>
          )}

          {/* Action Buttons */}
          {section === 'new' && selectedCount > 0 && (
            <div className="flex items-center gap-3" data-onboarding="bulk-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={onDownloadSelected}
                disabled={isDownloading}
                isLoading={isDownloading}
              >
                Download Selected ({selectedCount})
              </Button>
              <button
                onClick={onClearSelection}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
              >
                Clear
              </button>
            </div>
          )}

          {section === 'queue' && selectedCount > 0 && (
            <div className="flex items-center gap-3" data-onboarding="bulk-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={onTranslateSelected}
                disabled={isTranslating}
                isLoading={isTranslating}
              >
                Translate Selected ({selectedCount})
              </Button>
              <button
                onClick={onClearSelection}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
              >
                Clear
              </button>
            </div>
          )}

          {section === 'approved' && selectedCount > 0 && (
            <div className="flex items-center gap-3" data-onboarding="bulk-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={onUploadSelected}
                disabled={isUploading}
                isLoading={isUploading}
              >
                Upload Selected ({selectedCount})
              </Button>
              <button
                onClick={onClearSelection}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative" data-onboarding="search-input">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search cases..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 pr-4 py-2 w-64 text-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-elevated rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Refresh button */}
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">({refreshCountdown}s)</span>
          </button>
        </div>
      </div>
    </div>
  );
}

interface FilterButtonProps {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}

function FilterButton({ label, count, isActive, onClick }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        px-3 py-1 text-sm rounded-full transition-colors
        ${isActive
          ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-hover'
        }
      `}
    >
      {label} ({count})
    </button>
  );
}
